// The ONE schedule renderer (P1 Phase A.3). Draws whatever
// buildSchedule() (src/config/designSchedules.js) produced: title, source
// line, a plain table of published columns, provenance marks (Internal level
// only — buildSchedule already blanks them elsewhere), and qualifier notes as
// footnotes. Read-only by design: a schedule is a generated view of canonical
// data, edited where that data lives (room schedule, flyout, Basis of Design).
//
// Print: markup is plain <table> with class names so a print stylesheet
// (DesignSchedulesView.jsx) can lay it out for paper — no second PDF stack.
import { columnHeader } from "../config/designSchedules";

const th = { fontSize: 10.5, fontWeight: 700, color: "var(--text3)", textAlign: "right", padding: "6px 8px", textTransform: "uppercase", letterSpacing: "0.03em", borderBottom: "1px solid var(--cs-border)", whiteSpace: "nowrap", background: "var(--surface)" };
const td = { fontSize: 12, textAlign: "right", padding: "5px 8px", borderBottom: "1px solid var(--cs-border)", fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap", verticalAlign: "top" };

export default function DesignScheduleTable({ schedule, compact = false }) {
  if (!schedule) return null;
  const { title, source, columns, rows, notes, level } = schedule;
  return (
    <section className="design-schedule" data-schedule-key={schedule.key} style={{ marginBottom: compact ? 16 : 28, breakInside: "avoid" }}>
      <div className="design-schedule__header" style={{ display: "flex", alignItems: "baseline", gap: 10, marginBottom: 6 }}>
        <h3 style={{ fontSize: 14, fontWeight: 800, margin: 0 }}>{title}</h3>
        <span className="design-schedule__level" style={{ fontSize: 10.5, color: "var(--text3)", textTransform: "uppercase", letterSpacing: "0.05em" }}>{level}</span>
        {level === "internal" && source && <span className="design-schedule__source" style={{ fontSize: 11, color: "var(--text3)" }}>Source: {source}</span>}
      </div>
      <div style={{ overflowX: "auto", border: "1px solid var(--cs-border)", borderRadius: 8 }}>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr>
              {columns.map(col => (
                <th key={col.key} scope="col" style={{ ...th, textAlign: col.align || "right" }}>{columnHeader(col)}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr><td className="design-schedule__empty" colSpan={columns.length} style={{ ...td, textAlign: "left", color: "var(--text3)", fontStyle: "italic" }}>No rows — nothing in this revision feeds this schedule yet.</td></tr>
            )}
            {rows.map(row => (
              <tr key={row.id}>
                {columns.map(col => {
                  const c = row.cells[col.key];
                  return (
                    <td key={col.key} style={{ ...td, textAlign: col.align || "right", whiteSpace: col.format === "list" || col.align === "left" ? "normal" : "nowrap" }} title={c?.sourceLabel && level === "internal" ? c.sourceLabel : undefined}>
                      {c?.text ?? "—"}
                      {c?.mark && <sup className="design-schedule__mark" style={{ color: "#b45309", marginLeft: 1 }}>{c.mark}</sup>}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {notes?.length > 0 && (
        <ol className="design-schedule__notes" style={{ margin: "6px 0 0", paddingLeft: 18, fontSize: 10.5, color: "var(--text3)" }}>
          {notes.map((n, i) => <li key={i}>{n}</li>)}
        </ol>
      )}
    </section>
  );
}
